<script>
    import {onDestroy} from 'svelte';
    import {isAprilFoolsActive} from './isAprilFoolsActive.js';

    let { isStore = true } = $props();

    const today = new Date().toISOString().slice(0, 10);
    const active = isAprilFoolsActive() && localStorage.getItem('gravityDone') !== today;

    // phases: idle → tilting → falling → alert → stabilized → done
    let phase = $state(active ? 'idle' : 'done');

    let timeouts = [];

    function schedule(fn, ms) {
        const id = setTimeout(fn, ms);
        timeouts.push(id);
        return id;
    }

    function getCards() {
        return document.querySelectorAll('.pack-listing .grid > *, .pack-listing .calendar-grid > *, .pack-listing > *');
    }

    function assignRandomTransforms() {
        getCards().forEach(card => {
            card.style.setProperty('--tilt', `${(Math.random() - 0.5) * 10}deg`);
            card.style.setProperty('--fall-distance', `${200 + Math.random() * 350}px`);
            card.style.setProperty('--fall-tilt', `${(Math.random() - 0.5) * 30}deg`);
        });
    }

    function clearCardStyles() {
        getCards().forEach(card => {
            card.style.removeProperty('--tilt');
            card.style.removeProperty('--fall-distance');
            card.style.removeProperty('--fall-tilt');
        });
    }

    function handleStabilize() {
        if (phase !== 'alert') return;
        phase = 'stabilized';
        clearCardStyles();

        schedule(() => {
            phase = 'done';
            localStorage.setItem('gravityDone', today);
        }, 4000);
    }

    $effect(() => {
        if (phase !== 'idle') return;

        schedule(() => {
            assignRandomTransforms();
            phase = 'tilting';
        }, 2000);

        schedule(() => {
            phase = 'falling';
        }, 5000);

        schedule(() => {
            phase = 'alert';
        }, 7000);
    });

    onDestroy(() => {
        timeouts.forEach(clearTimeout);
        clearCardStyles();
    });
</script>

{#if phase !== 'done'}
    <!-- Alert overlay -->
    {#if phase === 'alert'}
        <div class="gravity-alert animate__animated animate__fadeInDown">
            <div class="alert-icon">&#9888;</div>
            <h2>SYSTEM ALERT</h2>
            <p class="alert-message">{isStore ? 'Gravitational anomaly detected in store module.' : 'Gravitational anomaly detected in supply depot.'}</p>
            <p class="alert-sub">{isStore ? 'Pack integrity compromised. Immediate action required.' : 'Supply integrity compromised. Immediate action required.'}</p>
            <button type="button" class="stabilize-btn" onclick={handleStabilize}>
                <span class="pulse-dot"></span>
                {isStore ? 'STABILIZE GRAVITATIONAL FIELD' : 'RESTORE SUPPLY LINES'}
            </button>
        </div>
    {/if}

    <!-- Reveal message -->
    {#if phase === 'stabilized'}
        <div class="gravity-reveal animate__animated animate__fadeIn">
            <h2>April Fools!</h2>
            <p>{isStore ? 'The store is fine. Your wallet, however...' : 'Your supplies are safe. Carry on, soldier.'}</p>
        </div>
    {/if}
{/if}

<!-- Global styles that target pack cards based on phase -->
<svelte:head>
    {#if phase === 'tilting'}
        {@html `<style id="gravity-fx">
            .pack-listing .grid > *, .pack-listing .calendar-grid > *, .pack-listing > * {
                transition: transform 1.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
                transform: rotate(var(--tilt, 0deg)) !important;
                transform-origin: bottom center !important;
            }
        </style>`}
    {:else if phase === 'falling'}
        {@html `<style id="gravity-fx">
            .pack-listing .grid > *, .pack-listing .calendar-grid > *, .pack-listing > * {
                transition: transform 2s cubic-bezier(0.55, 0.06, 0.68, 0.19), opacity 2s ease !important;
                transform: translateY(var(--fall-distance, 200px)) rotate(var(--fall-tilt, 10deg)) !important;
                transform-origin: bottom center !important;
                opacity: 0.7 !important;
                pointer-events: none !important;
            }
        </style>`}
    {:else if phase === 'alert'}
        {@html `<style id="gravity-fx">
            .pack-listing .grid > *, .pack-listing .calendar-grid > *, .pack-listing > * {
                transform: translateY(var(--fall-distance, 200px)) rotate(var(--fall-tilt, 10deg)) !important;
                transform-origin: bottom center !important;
                opacity: 0.5 !important;
                pointer-events: none !important;
            }
            .discount-banner { display: none !important; }
        </style>`}
    {:else if phase === 'stabilized'}
        {@html `<style id="gravity-fx">
            .pack-listing .grid > *, .pack-listing .calendar-grid > *, .pack-listing > * {
                transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease !important;
                transform: none !important;
                opacity: 1 !important;
            }
            .discount-banner { display: none !important; }
        </style>`}
    {/if}
</svelte:head>

<style lang="scss">
  .gravity-alert {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
    width: min(92vw, 560px);
    padding: 36px 32px 28px;
    text-align: center;
    color: #ffd6d6;
    background: linear-gradient(180deg, #3d0000 0%, #1a0000 100%);
    border: 3px solid #ff3333;
    border-radius: 16px;
    box-shadow:
      0 0 60px rgba(255, 0, 0, 0.35),
      0 0 120px rgba(255, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 100, 100, 0.15);
    animation: pulse-border 1.5s ease-in-out infinite alternate;
  }

  @keyframes pulse-border {
    from { border-color: #ff3333; box-shadow: 0 0 60px rgba(255, 0, 0, 0.35), 0 0 120px rgba(255, 0, 0, 0.15); }
    to { border-color: #ff6644; box-shadow: 0 0 80px rgba(255, 0, 0, 0.5), 0 0 160px rgba(255, 0, 0, 0.25); }
  }

  .alert-icon {
    font-size: 3.5rem;
    line-height: 1;
    margin-bottom: 8px;
    filter: drop-shadow(0 0 12px rgba(255, 200, 0, 0.6));
  }

  h2 {
    margin: 0 0 12px;
    font-family: var(--font-family-sans-condensed);
    font-size: clamp(1.8rem, 4vw, 2.6rem);
    font-weight: 900;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #ff4444;
    text-shadow: 0 0 20px rgba(255, 0, 0, 0.5);
  }

  .alert-message {
    margin: 0 0 4px;
    font-size: 1.3rem;
    color: #ffcccc;
  }

  .alert-sub {
    margin: 0 0 24px;
    font-size: 1.1rem;
    color: #ff9999;
    font-style: italic;
  }

  .stabilize-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 14px 32px;
    font-family: var(--font-family-sans-condensed);
    font-size: 1.2rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #fff;
    background: linear-gradient(145deg, #cc0000 0%, #880000 100%);
    border: 2px solid #ff6644;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(255, 0, 0, 0.4);
    transition: all 0.15s ease;

    &:hover {
      background: linear-gradient(145deg, #dd2222 0%, #aa0000 100%);
      box-shadow: 0 4px 30px rgba(255, 0, 0, 0.6);
      transform: scale(1.03);
    }

    &:active {
      transform: scale(0.97);
    }
  }

  .pulse-dot {
    width: 12px;
    height: 12px;
    background: #ff4444;
    border-radius: 50%;
    animation: dot-pulse 1s ease-in-out infinite;
    box-shadow: 0 0 8px #ff0000;
  }

  @keyframes dot-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(0.7); }
  }

  .gravity-reveal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
    width: min(92vw, 480px);
    padding: 36px 32px;
    text-align: center;
    color: #fff6da;
    background: linear-gradient(180deg, #1a3d00 0%, #0a2000 100%);
    border: 3px solid #66cc33;
    border-radius: 16px;
    box-shadow: 0 0 60px rgba(100, 200, 50, 0.3), 0 0 120px rgba(100, 200, 50, 0.1);

    h2 {
      color: #88ff44;
      text-shadow: 0 0 20px rgba(100, 255, 50, 0.5);
      margin-bottom: 8px;
    }

    p {
      margin: 0;
      font-size: 1.3rem;
      color: #ccff99;
    }
  }
</style>
